export class FocusHelper {
    /**
     * Selector used to locate the first form field on a page. Interactive
     * elements such as buttons and links are intentionally excluded so focus
     * lands on an actual input rather than a navigation/action button.
     */
    private static readonly focusableFieldSelector = [
        'input:not([type="hidden"]):not([disabled])',
        'textarea:not([disabled])',
        'select:not([disabled])',
        'mat-select:not([aria-disabled="true"])',
    ].join(', ');

    /**
     * Defers focusing the first field until after the current change-detection cycle, so
     * conditionally-rendered fields are in the DOM before we search. Skips focus if the host
     * is no longer connected (e.g. the user navigated away before the callback ran).
     *
     * @param host The host element to search within.
     */
    static deferFocusFirstField(host: HTMLElement | null | undefined): void {
        if (!host) {
            return;
        }

        setTimeout(() => {
            if (!host.isConnected) {
                return;
            }

            FocusHelper.focusFirstField(host);
        }, 0);
    }

    /**
     * Focuses the first visible, enabled form field found within the given host element.
     *
     * This is used to move the user's keyboard focus onto the first field of a page after
     * navigating to it (via Next, Previous, or the Step Menu), so users can continue
     * tabbing through fields without first having to click into the page.
     *
     * @param host The host element to search within.
     * @returns true if a field was found and focused, false otherwise.
     */
    static focusFirstField(host: HTMLElement | null | undefined): boolean {
        if (!host) {
            return false;
        }

        const candidates = Array.from(
            host.querySelectorAll<HTMLElement>(
                FocusHelper.focusableFieldSelector,
            ),
        );

        const target = candidates.find((element) =>
            FocusHelper.isVisibleAndEnabled(element),
        );

        if (!target) {
            return false;
        }

        target.focus({ preventScroll: true });

        return document.activeElement === target;
    }

    /**
     * Determines whether an element is currently visible and able to receive focus.
     */
    private static isVisibleAndEnabled(element: HTMLElement): boolean {
        if (
            element.hasAttribute('disabled') ||
            element.getAttribute('aria-disabled') === 'true'
        ) {
            return false;
        }

        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }

        // offsetParent is null for elements that are not rendered (e.g. inside a hidden container).
        // Elements with position: fixed actually have a null offsetParent, so they are excluded from this check.
        if (element.offsetParent === null && style.position !== 'fixed') {
            return false;
        }

        return true;
    }
}
